import type { CartItems, V2CartItem } from '@/core/cart/cart.store'
import type { FGProducts } from '@/core/feelgreat/feelgreat.types'

export const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const parseJson = (value: unknown): unknown => {
    if (typeof value !== 'string') return value
    try {
        return JSON.parse(value)
    } catch {
        return undefined
    }
}

const getNestedValue = (source: Record<string, unknown>, path: string[]): unknown =>
    path.reduce<unknown>((current, key) => {
        if (isRecord(current) && key in current) return current[key]
        return undefined
    }, source)

export const getShipmentPacks = (raw: Record<string, unknown>): unknown =>
    parseJson(
        getNestedValue(raw, ['ushop', 'detail_page', 'shipment_packs']) ??
            getNestedValue(raw, ['ushop', 'u23', 'detail_page', 'shipment_packs']) ??
            getNestedValue(raw, ['ushop', 'shipment_packs']) ??
            raw.shipment_packs
    )

export const normalizePurchaseType = (purchaseType: string) => {
    if (purchaseType === 'subscription') return 'subscribe'
    if (purchaseType === 'oneTime') return 'onetime'
    return purchaseType
}

export const findProductByFlavorOrItemCode = (fgProducts: FGProducts, purchaseType: string, key: string) => {
    const direct = fgProducts?.[key]?.[purchaseType]
    if (direct) return { flavor: key, product: direct }

    for (const [flavor, purchaseTypes] of Object.entries(fgProducts)) {
        const product = purchaseTypes[purchaseType]
        if (product?.item_code === key) return { flavor, product }
    }
    return null
}

export const isKnownCartItemCode = (fgProducts: FGProducts, itemCode: string) => {
    for (const purchaseTypes of Object.values(fgProducts)) {
        for (const product of Object.values(purchaseTypes)) {
            if (product.item_code === itemCode) return true
        }
    }
    return false
}

export const toCartItemsFromShipmentPacks = (packs: Record<string, unknown>, fgProducts: FGProducts): CartItems => {
    const cart: CartItems = {}
    for (const [rawPurchaseType, bucket] of Object.entries(packs)) {
        if (!isRecord(bucket)) continue
        const purchaseType = normalizePurchaseType(rawPurchaseType)
        const flavors: Record<string, number> = {}
        for (const [key, qtyValue] of Object.entries(bucket)) {
            const qty = Number(qtyValue)
            if (qty <= 0) continue
            const flavor = findProductByFlavorOrItemCode(fgProducts, purchaseType, key)?.flavor ?? key
            flavors[flavor] = qty
        }
        if (Object.keys(flavors).length > 0) cart[purchaseType] = flavors
    }
    return cart
}

export const isV2CartItem = (value: unknown): value is V2CartItem =>
    isRecord(value) && typeof value.product_id === 'string' && isRecord(value.selection) && typeof value.purchase_option === 'string' && Number(value.qty) > 0
